import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { tabStorage } from './safeStorage.ts';

import type {
  ContainerView,
  OrgListItem,
  ServerWorkspaceDetail,
} from '../features/onboarding/api.ts';

// The workspace onboarding created, remembered in this browser. The backend
// owns the record; this is the view the shell needs. It grants nothing.

export interface ActorProfile {
  id: string;
  providerIssuer: string;
  providerSubject: string;
  email: string;
  displayName?: string | null;
  createdAt: string;
  updatedAt?: string;
}

export interface WorkspaceListItem {
  id: string;
  name: string;
  domain: string;
  role: string;
  status: string;
  verifiedAt: string;
  createdAt: string;
  organizationId?: string | null;
  organizationName?: string | null;
  permissions?: string[];
  via?: 'workspace' | 'organization';
}

export interface WorkspaceMandateSummary {
  status: 'active' | 'expired' | 'revoked' | 'stale_proof' | 'missing';
  letterId?: string;
  endsAt?: string;
  proofCheckedAt?: string;
  revokedAt?: string;
}

export interface WorkspaceSession {
  /** Backend workspace id. */
  id: string;
  setupSessionId?: string;
  authorizationId?: string;
  authorizationConfirmedAt?: string;
  authorizer?: { name: string; email: string };
  name: string;
  domain: string;
  verifiedThrough: string;
  verifiedAt: string;
  consent: { user: string; time: string };
  container?: ContainerView;
  mandate?: WorkspaceMandateSummary;
  role?: string;
  membershipStatus?: string;
  organization?: { id: string; name: string } | null;
  /** Absent in sessions saved before permissions existed; see features/auth/permissions.ts. */
  permissions?: string[];
  via?: 'workspace' | 'organization';
}

interface SessionState {
  workspace: WorkspaceSession | null;
  actor: ActorProfile | null;
  workspaces: WorkspaceListItem[];
  /** The organizations the switcher lists; fetched again whenever it opens. */
  orgs: OrgListItem[];
  /** The workspace last open in each organization, so switching organization returns to it. */
  lastByOrg: Record<string, string>;
  /** The workspace being opened in place, for the top bar while it checks. */
  switching: { id: string; name: string; domain: string } | null;
  /** Why the last switch didn't happen, shown above the page until dismissed. */
  notice: { title: string; message: string } | null;
  /** Set when the user has just left onboarding; drives the one-time entrance. */
  justOnboarded: boolean;
  start: (workspace: WorkspaceSession) => void;
  setWorkspace: (workspace: WorkspaceSession | null) => void;
  setActor: (actor: ActorProfile | null) => void;
  setWorkspaces: (workspaces: WorkspaceListItem[]) => void;
  setOrgs: (orgs: OrgListItem[]) => void;
  /** Ignored when it belongs to a workspace other than the open one (a poll that outlived it). */
  setContainer: (container: ContainerView, workspaceId: string) => void;
  setSwitching: (switching: SessionState['switching']) => void;
  setNotice: (notice: SessionState['notice']) => void;
  clearEntrance: () => void;
  signOut: () => void;
}

// Opening a workspace remembers it as its organization's last one.
const opened = (s: SessionState, workspace: WorkspaceSession | null) => ({
  workspace,
  lastByOrg: workspace?.organization
    ? { ...s.lastByOrg, [workspace.organization.id]: workspace.id }
    : s.lastByOrg,
});

/** The session view of a workspace the server just described. */
export function workspaceSessionFrom(
  detail: ServerWorkspaceDetail,
  email: string,
): WorkspaceSession {
  return {
    id: detail.id,
    name: detail.name,
    domain: detail.domain,
    verifiedAt: detail.verifiedAt,
    verifiedThrough: detail.verifiedThrough,
    consent: { user: email, time: detail.createdAt },
    authorizationId: detail.mandate?.letterId,
    authorizationConfirmedAt: detail.mandate?.proofCheckedAt ?? detail.verifiedAt,
    mandate: detail.mandate,
    role: detail.role,
    membershipStatus: detail.membershipStatus,
    organization: detail.organization,
    permissions: detail.permissions,
    via: detail.via,
    container: detail.container,
  };
}

export const useSession = create<SessionState>()(
  persist(
    (set) => ({
      workspace: null,
      actor: null,
      workspaces: [],
      orgs: [],
      lastByOrg: {},
      switching: null,
      notice: null,
      justOnboarded: false,
      start: (workspace) => set((s) => ({ ...opened(s, workspace), justOnboarded: true })),
      setWorkspace: (workspace) => set((s) => opened(s, workspace)),
      setActor: (actor) => set({ actor }),
      setWorkspaces: (workspaces) => set({ workspaces }),
      setOrgs: (orgs) => set({ orgs }),
      setContainer: (container, workspaceId) =>
        set((s) =>
          s.workspace?.id === workspaceId ? { workspace: { ...s.workspace, container } } : {},
        ),
      setSwitching: (switching) => set({ switching }),
      setNotice: (notice) => set({ notice }),
      clearEntrance: () => set({ justOnboarded: false }),
      signOut: () =>
        set({
          workspace: null,
          actor: null,
          workspaces: [],
          orgs: [],
          lastByOrg: {},
          notice: null,
          justOnboarded: false,
        }),
    }),
    {
      name: 'traceira-session',
      version: 3,
      // v3 only added optional fields (organization, permissions, via, displayName): v2 carries over;
      // older versions are dropped as before.
      migrate: (persisted, version) => (version === 2 ? persisted : {}) as SessionState,
      storage: createJSONStorage(() => tabStorage('traceira-session')),
      partialize: ({ workspace, actor, workspaces, orgs, lastByOrg }) => ({
        workspace,
        actor,
        workspaces,
        orgs,
        lastByOrg,
      }),
    },
  ),
);
